import { FedExIcon, MxShipping, USPSIcon } from '@c/icons';
import { UPS } from '@c/icons/carriers';
import { PencilIcon, PlusCircleIcon } from '@heroicons/react/20/solid';
import { AddressDocument } from '@models/address';
import SafeImage from '@ui/SafeImage';
import { matchCartItemVariationsToProductVariation } from '@util/firestore/cart';
import {
  isDomestic,
  Rate,
  shouldExcludeCountry,
} from '@util/firestore/shipengine';
import { useRates } from '@util/hooks/useRates';
import { capitalize, formatCurrency } from '@util/index';
import { useShoppingCart } from 'context/ShoppingCartContext';
import { ProductDocument } from 'models/product';
import { useEffect, useState } from 'react';

function ItemShippingSelect({
  product,
  shipment,
  shipTo,
  hideProduct = false,
  chooseCheapestByDefault = false,
  hide = false,
  quantity,
  onOptionSelected,
  selectedRateId,
  offer_amount,
  customsInfo,
  onError,
  onRatesLoaded,
}: {
  product?: ProductDocument;
  shipment?: ProductDocument['shipment'];
  shipTo: AddressDocument;
  hideProduct?: boolean;
  chooseCheapestByDefault?: boolean;
  hide?: boolean;
  quantity?: number;
  onOptionSelected?: ({
    rate,
    product_id,
    seller_id,
  }: {
    rate: Rate;
    product_id: string;
    seller_id: string;
  }) => void;
  selectedRateId: string | null;
  offer_amount?: number;
  customsInfo?: {
    product_title: string;
    product_category: string;
    product_sku: string;
    product_price: number;
  };
  onError?: (error: Error) => void;
  onRatesLoaded?: () => void;
}) {
  const { cart } = useShoppingCart();
  const { rates, ratesError, ratesLoading, selectOption, getFlatRateShipping } =
    useRates({
      product,
      shipment,
      shipTo,
      chooseCheapestByDefault,
      offer_amount,
      customsInfo,
      onOptionSelected,
      selectedRateId,
      quantity,
    });

  const [showShippingOptions, setShowShippingOptions] = useState(false);

  useEffect(() => {
    if (ratesError) {
      onError?.(ratesError as Error);
    }
  }, [ratesError, onError]);

  useEffect(() => {
    if (rates?.length && !ratesLoading) {
      onRatesLoaded?.();
    }
  }, [rates, ratesLoading, onRatesLoaded]);

  if (hide) return null;

  const handleOptionSelect = (option: Rate) => {
    selectOption(option);

    onOptionSelected?.({
      rate: option,
      product_id: product?.id ?? '',
      seller_id: product?.seller_id ?? '',
    });
  };

  const flatRate = getFlatRateShipping();

  const selectedRate = rates?.find((r) => r.rate_id === selectedRateId);
  const delivery_days =
    selectedRate?.delivery_days ??
    (rates?.find((r) => r.service_code === 'ups_ground')?.delivery_days ?? 5) +
      1;

  const item = cart?.items.find((i) => i.product_id === product?.id);

  const variants =
    product && item
      ? matchCartItemVariationsToProductVariation({
          cartItem: item,
          product,
        })
      : [];
  const imageSource = variants?.[0]?.image || product?.thumbnail;
  return (
    <div className="h-full">
      {product && !hideProduct && (
        <div className={`my-4 flex items-center gap-x-4`}>
          <SafeImage
            alt={product.title}
            key={imageSource}
            src={imageSource}
            height={100}
            width={100}
            className="aspect-square h-32 w-32 rounded-2xl object-contain"
          />

          <div className={`flex w-full items-start justify-between`}>
            <div className="flex flex-col gap-y-2">
              <h5 className="line-clamp-1 w-[25rem] whitespace-pre-wrap text-[1.6rem] font-semibold sm:w-[28rem]">
                {product.title}
              </h5>

              <div className="flex flex-col items-start gap-2">
                <ul className="text-[1.6rem] font-medium text-brand-dark-gray">
                  <li>
                    {product.category1 === 'Boots' ||
                    product.category1 === 'Helmets'
                      ? product.category1
                      : product.category2
                      ? product.category2
                      : product.location_display
                      ? product.location_display
                      : product.category1
                      ? product.category1
                      : product.category}
                  </li>
                  {item?.size?.letter && <li>Size: {item.size.letter}</li>}
                  {item?.size?.number && <li>Size: {item.size.number}</li>}
                  {item?.color && <li>Color: {item.color}</li>}
                </ul>
              </div>
            </div>

            <div className="flex flex-col items-end gap-y-2">
              <p className="text-[1.6rem] font-semibold">
                {formatCurrency(product.price)}
              </p>

              {item && (
                <p className="text-[1.6rem] text-zinc-500">Qty: {item.qty}</p>
              )}
            </div>
          </div>
        </div>
      )}
      {ratesLoading && (
        <div className="flex h-[6rem] w-full gap-4 rounded-[1rem]">
          <span className="flex h-[6rem] grow animate-pulse items-center justify-center rounded-[1rem] bg-gray-200 text-[1.8rem] text-black">
            <span className="font-medium">Loading Shipping Rates...</span>
          </span>
        </div>
      )}
      {!ratesLoading &&
        (product?.is_flat_rate || isDomestic(product?.shipment, shipTo)) &&
        flatRate &&
        !shouldExcludeCountry(product?.shipment, shipTo) && (
          <div className="flex items-center justify-between gap-x-4 rounded-[2rem] bg-gray-100 p-6">
            <div className="flex items-center gap-[1.6rem]">
              <div className="w-[5rem] shrink-0">
                <MxShipping width={29} height={21} />
              </div>
              <div className="flex flex-col">
                <p className="text-[1.6rem] font-semibold text-black">
                  Flat Rate
                </p>
                <p className="text-[1.4rem] text-brand-gray">
                  Shipping carrier selected by seller
                </p>
              </div>
            </div>

            <div className="text-[1.6rem] font-semibold text-black">
              {flatRate}
            </div>
          </div>
        )}
      {!ratesLoading &&
        !product?.is_flat_rate &&
        !isDomestic(product?.shipment, shipTo) &&
        !shouldExcludeCountry(product?.shipment, shipTo) && (
          <div>
            {!ratesLoading && (
              <button
                className={`flex w-full gap-[1.6rem] rounded-[2rem] bg-gray-100 p-6`}
                type="button"
                onClick={() => setShowShippingOptions(!showShippingOptions)}
                aria-label="Select Shipping"
              >
                {selectedRate ? (
                  <>
                    <div className="w-[5rem] shrink-0">
                      {selectedRate.carrier_code === 'fedex' ? (
                        <FedExIcon className="h-16 w-16" />
                      ) : selectedRate.carrier_code === 'ups' ? (
                        <UPS className="h-16 w-16" />
                      ) : (
                        <USPSIcon className="h-16 w-16" />
                      )}
                    </div>

                    <div className="flex grow items-center gap-5">
                      <div className="flex flex-col items-start gap-y-2">
                        <p className="text-[1.6rem] font-semibold text-black">
                          {delivery_days} {delivery_days === 1 ? 'Day' : 'Days'}
                        </p>

                        <p className="text-left text-[1.4rem] text-brand-gray">
                          {selectedRate.service_type}
                        </p>
                        {selectedRate.package_type &&
                          selectedRate.package_type !== 'package' && (
                            <p className="text-[1.4rem] text-brand-gray">
                              {selectedRate.package_type
                                .split('_')
                                .map(capitalize)
                                .join(' ')}
                            </p>
                          )}
                        {!!selectedRate.tax_amount?.amount && (
                          <p className="text-[1.4rem] text-brand-gray">
                            Est. Duties:{' '}
                            {formatCurrency(selectedRate.tax_amount.amount)}
                          </p>
                        )}
                      </div>

                      <div className="ml-auto text-[1.6rem] font-semibold text-black">
                        {formatCurrency(selectedRate.total_amount)}
                      </div>

                      <PencilIcon
                        width={20}
                        height={20}
                        className="text-brand-secondary"
                      />
                    </div>
                  </>
                ) : (
                  <div className="flex w-full items-center justify-between gap-y-2">
                    <p className="text-[1.75rem] font-semibold">
                      Select Shipping
                    </p>
                    <PlusCircleIcon className="h-16 w-16 text-brand-secondary" />
                  </div>
                )}
              </button>
            )}

            {showShippingOptions && (
              <div className="mt-4 flex flex-col gap-y-4">
                {!!rates?.length &&
                  rates.map((option) => {
                    const delivery_days =
                      option.delivery_days ??
                      (rates.find((r) => r.service_code === 'ups_ground')
                        ?.delivery_days ?? 5) + 1;
                    const isSelected = option.rate_id === selectedRateId;

                    if (option.rate_id === selectedRateId) return null;

                    return (
                      <button
                        className={`flex w-full gap-[1.6rem] rounded-[2rem] bg-[#F6F6F6] p-6 transition-all duration-150 ease-linear ${
                          isSelected
                            ? 'border-2 border-brand-secondary'
                            : 'hover:bg-[#E7E7E7]'
                        }`}
                        type="button"
                        onClick={() => {
                          handleOptionSelect(option);
                          setShowShippingOptions(false);
                        }}
                        key={option.rate_id}
                      >
                        <div className="w-[6rem]">
                          {option.carrier_code === 'fedex' ? (
                            <FedExIcon className="h-16 w-16" />
                          ) : option.carrier_code === 'ups' ? (
                            <UPS className="h-16 w-16" />
                          ) : (
                            <USPSIcon className="h-16 w-16" />
                          )}
                        </div>

                        <div className="flex grow items-center gap-5">
                          <div className="flex flex-col items-start">
                            <h5 className="text-[1.6rem] font-semibold text-brand-secondary">
                              {delivery_days}{' '}
                              {delivery_days === 1 ? 'Day' : 'Days'}
                            </h5>

                            <p className="text-[1.4rem] text-brand-gray">
                              {option.service_type}
                            </p>
                            {option.package_type &&
                              option.package_type !== 'package' && (
                                <p className="text-[1.4rem] text-brand-gray">
                                  {option.package_type
                                    .split('_')
                                    .map(capitalize)
                                    .join(' ')}
                                </p>
                              )}
                            {!!option.tax_amount?.amount && (
                              <p className="text-[1.4rem] text-brand-gray">
                                Est. Duties:{' '}
                                {formatCurrency(option.tax_amount.amount)}
                              </p>
                            )}
                          </div>

                          <div className="ml-auto text-[1.6rem] font-semibold text-black">
                            {formatCurrency(option.total_amount)}
                          </div>
                        </div>
                      </button>
                    );
                  })}
              </div>
            )}
          </div>
        )}
      {!ratesLoading &&
        (shouldExcludeCountry(product?.shipment, shipTo) ||
          (product?.is_flat_rate && flatRate === null)) && (
          <div className="text-center text-brand-red">
            Shipping to this region is not available
          </div>
        )}
      {(ratesError as Error) && !ratesLoading && (
        <div className="text-center text-brand-red">
          {(ratesError as Error).message}
        </div>
      )}
      {rates === null && !ratesError && !ratesLoading && (
        <div>
          <h5 className=" text-[1.6rem] font-semibold">
            Error: No Shipping Available
          </h5>
          <p className="text-[1.4rem] text-brand-gray">
            Please contact the seller to add shipping to your country
          </p>
        </div>
      )}
    </div>
  );
}

export default ItemShippingSelect;
